import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useContacts from "../../../services/chat/useContacts";
import useChats from "../../../services/chat/useChats";
import useMedia from "../../../services/chat/useMedia";
import useTemplates from "../../../services/Whatsapp/useTemplates";

import ContactAdd from "./ContactAdd";
import ChatWindow from "./ChatWindow";
import MessageInput from "./MessageInput";
import MediaLibraryModal from "./MediaLibraryModal";
import TemplateModal from "./TemplateModal";
import SendTemplateModal from "./SendTemplateModal";

import wpImage from "../../../images/Whatsapp.svg";
import whatsappBg from "../../../images/chat_BG.png";
import EditContactModal from "./EditContactModal";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import notificationSound from "../../../Sound/messageTone.mp3";
import { Pencil, RefreshCw, UserCheck } from "lucide-react";
import { ReactComponent as Person } from "../../../images/Person.svg";

/* ─── tokens ─── */
const C = {
  green: "#25d366",
  greenDk: "#128c7e",
  bg: "#ffffff",
  white: "#ffffff",
  border: "#d1d5db",
  text: "#111b21",
  sub: "#667781",
  muted: "#8696a0",
  red: "#e53e3e",
};

/* ─── delete confirm modal ─── */
const DeleteModal = ({ contact, onConfirm, onCancel }) => (
  <div
    className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45"
    onClick={onCancel}
  >
    <div
      className="rounded-[14px] p-[28px_32px] w-[340px] shadow-[0_8px_32px_rgba(0,0,0,0.18)] text-center"
      style={{ background: C.white }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* icon */}
      <div className="w-[52px] h-[52px] rounded-full bg-[#fff0f0] flex items-center justify-center mx-auto mb-4">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.red}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
          <path d="M9 6V4h6v2" />
        </svg>
      </div>
      <p className="text-[16px] font-bold mb-2" style={{ color: C.text }}>
        Are you sure?
      </p>
      <p className="text-[13.5px] mb-6 leading-[1.5]" style={{ color: C.sub }}>
        Do you want to delete{" "}
        <strong>{contact?.contact_name || contact?.contact_number}</strong>?
        This action cannot be undone.
      </p>
      <div className="flex gap-2.5">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-lg text-[14px] font-medium cursor-pointer"
          style={{
            border: `1px solid ${C.border}`,
            background: C.white,
            color: C.sub,
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 py-2.5 rounded-lg border-0 text-[14px] font-semibold cursor-pointer shadow-[0_2px_8px_rgba(229,62,62,0.3)]"
          style={{
            background: C.red,
            color: C.white,
          }}
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
);

const avatarColors = [
  "bg-red-400",
  "bg-blue-400",
  "bg-green-400",
  "bg-yellow-400",
  "bg-purple-400",
  "bg-pink-400",
  "bg-indigo-400",
  "bg-cyan-400",
  "bg-orange-400",
  "bg-teal-400",
];

/* ─── Avatar ─── */
const getAvatarColor = (name = "") => {
  let hash = 0;

  for (let i = 0; i < name.length; i++) {
    hash += name.charCodeAt(i);
  }

  return avatarColors[hash % avatarColors.length];
};

const Chats = ({ userID }) => {
  const {
    contacts,
    chatContacts,
    setChatContacts,
    fetchChatContacts,
    searchChatContacts,
    deleteContact,
    loading,
  } = useContacts();

  const {
    chats,
    people,
    labels,
    loadingChats,
    fetchChats,
    sendMessage,
    getAllUserList,
    fetchLabels,
    editContact,
    assignLabelToChat,
    assignTeamMemberToChat,
  } = useChats();

  const { mediaList, fetchUploadedMedia, sendMedia, loadingMedia } =
    useMedia(userID);

  const {
    templates,
    fetchVendorTemplates,
    deleteVendorTemplate,
    fetchTemplates,
    executeTemplate,
  } = useTemplates(userID);

  const { whatsappNumbers } = useWhatsappNumbers();

  const [activeTab, setActiveTab] = useState("chats");
  const [prevTab, setPrevTab] = useState("chats"); // track previous tab for "add"
  const [search, setSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [mediaType, setMediaType] = useState("");
  const [chatHasMore, setChatHasMore] = useState(true);

  /* delete modal */
  const [deleteTarget, setDeleteTarget] = useState(null); // contact to delete
  const [deleting, setDeleting] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedPerson, setSelectedPerson] = useState("");
  const [SingleWhatsApp, setSingleWhatsApp] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSendTemplateModal, setShowSendTemplateModal] = useState(false);
  const [contactPage, setContactPage] = useState(1);
  const [refreshingContacts, setRefreshingContacts] = useState(false);
  const [contactHasMore, setContactHasMore] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFetchingContacts = useRef(false);
  const contactPageRef = useRef(1);
  const audioRef = useRef(null);
  const searchRef = useRef(null);

  const showThreePanel = selectedContact !== null && activeTab !== "add";

  // delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (activeTab === "add") return;

    const query = debouncedSearch.trim();

    if (!query) {
      setContactPage(1);
      contactPageRef.current = 1;
      setContactHasMore(true);
      fetchChatContacts(1);
      return;
    }

    if (query.length < 3) return;

    setContactPage(1);
    contactPageRef.current = 1;
    setContactHasMore(false);
    searchChatContacts(query);
  }, [debouncedSearch, activeTab]);

  const fetchMoreContacts = async () => {
    if (loading || !contactHasMore || isFetchingContacts.current) return;

    isFetchingContacts.current = true;

    const nextPage = contactPageRef.current + 1;

    const res = await fetchChatContacts(nextPage);

    if (!res?.data || res.data.length === 0) {
      setContactHasMore(false);
    } else {
      contactPageRef.current = nextPage;
      setContactPage(nextPage);
    }

    isFetchingContacts.current = false;
  };

  const handleRefreshContacts = async (showMessage = true) => {
    if (refreshingContacts || loading) return;

    try {
      setRefreshingContacts(true);

      // Reset pagination
      setContactPage(1);
      contactPageRef.current = 1;
      setContactHasMore(true);
      isFetchingContacts.current = false;

      const currentSearch = search.trim();

      // Clear cached contacts
      sessionStorage.removeItem(`chat_contacts_${userID}`);

      let response;

      if (currentSearch.length >= 3) {
        setContactHasMore(false);
        response = await searchChatContacts(currentSearch);
      } else {
        response = await fetchChatContacts(1, 50, true);
      }

      if (showMessage) {
        if (response) {
          // toast.success("Chats refreshed successfully");
        } else {
          toast.error("Unable to refresh chats");
        }
      }

      return response;
    } catch (error) {
      console.error("Refresh contacts error:", error);

      if (showMessage) {
        toast.error("Unable to refresh chats");
      }

      return null;
    } finally {
      setRefreshingContacts(false);
    }
  };

  const [chatPage, setChatPage] = useState(1);

  const fetchMoreChats = async () => {
    if (!selectedContact || loadingChats || !chatHasMore) return;

    const nextPage = chatPage + 1;

    const res = await fetchChats(selectedContact.contact_number, nextPage);

    if (!res?.data || res.data.length === 0) {
      setChatHasMore(false);
      return;
    }

    setChatPage(nextPage);
  };

  const handleAssign = async (personId) => {
    if (!selectedContact || !personId) return;

    const selectedUser = people.find(
      (u) => String(u.userid) === String(personId),
    );

    const res = await assignTeamMemberToChat(selectedContact, personId);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage || "Chat assigned successfully");

      setSelectedPerson(personId);

      const assignedValue = selectedUser
        ? `${selectedUser.user_name}|${selectedUser.userid}`
        : "";

      setSelectedContact((prev) => ({
        ...prev,
        assigned_user: assignedValue,
      }));

      setChatContacts((prev) =>
        prev.map((chat) =>
          chat.id === selectedContact.id
            ? {
                ...chat,
                assigned_user: assignedValue,
              }
            : chat,
        ),
      );
    } else {
      toast.error(res?.responseMessage || "Assign failed");
    }
  };

  const handleAddLabel = async (labelTitle) => {
    if (!selectedContact || !labelTitle) return;

    const selectedLabelObj = labels.find((l) => l.title === labelTitle);

    const res = await assignLabelToChat(selectedContact, labelTitle);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage || "Label assigned successfully");

      setSelectedLabel(labelTitle);

      const updatedLabel = selectedLabelObj || { title: labelTitle };

      setSelectedContact((prev) => ({
        ...prev,
        label: updatedLabel,
      }));

      setChatContacts((prev) =>
        prev.map((chat) =>
          chat.id === selectedContact.id
            ? {
                ...chat,
                label: updatedLabel,
              }
            : chat,
        ),
      );
    } else {
      toast.error(res?.responseMessage || "Label assign failed");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];

      const payload = {
        mobile: selectedContact?.contact_number,
        mediaData: base64,
        mediaType: file.type.startsWith("image")
          ? "img"
          : file.type.startsWith("video")
            ? "video"
            : "pdf",
        message: "",
      };

      try {
        const res = await sendMedia(payload);

        if (res?.responseCode === "00") {
          toast.success("Media sent successfully ");
        } else {
          toast.error(res?.responseMessage || "Failed to send media ");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong ");
      }
    };

    reader.readAsDataURL(file);
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setSearch("");

    if (tab === "chats" || tab === "contacts") {
      setContactPage(1);
      contactPageRef.current = 1;
      setContactHasMore(true);

      sessionStorage.removeItem(`chat_contacts_${userID}`);
      await fetchChatContacts(1, 50, true);
    }

    if (tab === "add") {
      setSelectedContact(null);
    }
  };

  const lastMsgRef = useRef("");

  useEffect(() => {
    if (!userID || !selectedContact?.contact_number) return;

    const socket = new WebSocket(
      `wss://payinnew.remittpe.com/ws?userId=${userID}&roomId=${selectedContact.contact_number}`,
    );

    socket.onopen = () => {
      console.log("Chat WebSocket Connected:", selectedContact.contact_number);
    };

    socket.onmessage = (event) => {
      try {
        if (lastMsgRef.current === event.data) return;
        lastMsgRef.current = event.data;

        const message = JSON.parse(event.data);
        fetchChats(selectedContact.contact_number, 1);

        audioRef.current?.play().catch(() => {});
      } catch (error) {
        console.error("Invalid WS message:", event.data);
      }
    };

    return () => socket.close();
  }, [selectedContact?.contact_number, userID]);

  useEffect(() => {
    getAllUserList();
    fetchLabels();
  }, []);

  /* ── contact click ── */
  const handleSelectContact = (contact) => {
    if (activeTab === "add") return;

    setSelectedContact(contact);

    // Set selected label
    setSelectedLabel(contact?.label?.title || "");

    // Set selected assigned user
    const assignedUser = people.find(
      (user) => user.user_name === (contact?.assigned_user || "").split("|")[0],
    );

    setSelectedPerson(assignedUser ? String(assignedUser.userid) : "");

    setChatHasMore(true);
    setChatPage(1);

    fetchChats(contact.contact_number, 1);
  };

  const handleSendMessage = async (msg) => {
    if (!selectedContact) return;
    await sendMessage(selectedContact.contact_number, msg);
    fetchChats(selectedContact.contact_number);
  };

  /* ── delete ── */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const data = await deleteContact(deleteTarget.contact_number);
      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Contact deleted successfully");
        fetchChatContacts();
      } else {
        toast.error(data?.responseMessage || "Delete failed");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  /* ── data for each tab ── */
  const filterFn = (list) => {
    const q = search.toLowerCase();
    if (!q) return list || [];
    return (list || []).filter(
      (c) =>
        (c.contact_name || "").toLowerCase().includes(q) ||
        (c.contact_number || "").includes(q),
    );
  };

  const chatFiltered = filterFn(chatContacts);
  const contactFiltered = filterFn(chatContacts);
  const addSourceList = contactFiltered;

  const chatPaged = chatFiltered;
  const contactPaged = contactFiltered;
  const addPaged = addSourceList;

  const formatTime = (dt) => {
    if (!dt) return "";

    return new Date(dt).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* ── shared row styles ── */
  const rowStyle = (selected) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    padding: "0px 0 8px 0",
    borderBottom: `1px solid ${C.border}`,
    cursor: "pointer",
    background: selected ? "#f0fdf4" : C.white,
    // borderLeft: selected ? `3px solid ${C.green}` : "0px solid transparent",
    transition: "background .1s",
  });

  useEffect(() => {
    if (!SingleWhatsApp) return;
    fetchVendorTemplates(SingleWhatsApp);
  }, [SingleWhatsApp]);

  useEffect(() => {
    const audio = new Audio(notificationSound);
    audio.preload = "auto";
    audio.loop = false;
    audio.volume = 1.0;

    audioRef.current = audio;

    const unlockAudio = () => {
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.muted = false;
          localStorage.setItem("soundUnlocked", "true");
        })
        .catch(() => {});

      document.removeEventListener("click", unlockAudio);
    };

    if (localStorage.getItem("soundUnlocked") !== "true") {
      document.addEventListener("click", unlockAudio, { once: true });
    }

    return () => {
      document.removeEventListener("click", unlockAudio);
    };
  }, []);

  const lastGlobalMsg = useRef("");

  useEffect(() => {
    if (!userID) return;

    const socket = new WebSocket(
      `wss://payinnew.remittpe.com/ws?userId=${userID}&roomId=new_conversation`,
    );

    let isConnected = false;

    socket.onopen = () => {
      isConnected = true;
    };

    socket.onmessage = (event) => {
      try {
        if (!isConnected) return;
        if (lastGlobalMsg.current === event.data) return;
        lastGlobalMsg.current = event.data;

        JSON.parse(event.data);

        if (activeTab === "chats") {
          fetchChatContacts(1, 50, true);
        }

        audioRef.current?.play().catch(() => {});
      } catch (error) {
        console.error("Invalid JSON message:", event.data);
      }
    };

    return () => socket.close();
  }, [userID, activeTab]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  return (
    <>
      <div
        className="montserrat-font-1 grid h-full w-full bg-[#f5f7fb]"
        style={{
          gridTemplateColumns: showThreePanel ? "390px 1fr 320px" : "390px 1fr",
          gridTemplateRows: showThreePanel ? "56px 1fr" : "1fr",
        }}
      >
        {/* ══════════ LEFT PANEL ══════════ */}
        <div
          className="col-[1] row-[1/4] bg-white shadow-sm flex flex-col h-full overflow-hidden"
          style={{
            background: C.white,
            borderRight: `1px solid ${C.border}`,
          }}
        >
          {/* ── Tabs ── */}
          <div
            className="flex shrink-0"
            style={{ borderBottom: `1px solid ${C.border}` }}
          >
            {[
              ["chats", "Chats"],
              ["contacts", "Contacts"],
              ["add", "+ Add"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => handleTabChange(v)}
                className="flex-1 py-3 text-[13.5px] font-medium border-0 bg-transparent cursor-pointer"
                style={{
                  borderBottom:
                    activeTab === v
                      ? `2.5px solid ${C.green}`
                      : "2.5px solid transparent",
                  color: activeTab === v ? C.green : C.sub,
                }}
              >
                {l}
              </button>
            ))}
          </div>

          {/* ── Search ── */}
          <div
            className="px-4 pt-3 shrink-0"
            style={{ borderBottom: `1px solid ${C.border}` }}
          >
            <div className="flex items-center h-10 px-3 bg-white border border-gray-300 rounded-md">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-gray-400 mr-2 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>

              <form autoComplete="off" className="w-full">
                <input
                  ref={searchRef}
                  type="text"
                  name="chatSearch"
                  autoComplete="off"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-[14px] placeholder:text-gray-400"
                  style={{ color: C.text }}
                />
              </form>
            </div>

            <div className="flex justify-between items-center gap-3.5 pt-[9px] pb-[5px] shrink-0">
              <span
                className="text-[11.5px] font-bold uppercase tracking-[0.5px]"
                style={{ color: C.sub }}
              >
                Recent Chats
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <button
                  onClick={() => {
                    const phoneId =
                      SingleWhatsApp ||
                      (whatsappNumbers?.length > 0
                        ? whatsappNumbers[0].phone_no_id
                        : "");

                    if (!phoneId) {
                      console.error("No WhatsApp number found");
                      return;
                    }

                    setSingleWhatsApp(phoneId);
                    fetchVendorTemplates(phoneId);

                    setShowSendTemplateModal(true);
                  }}
                  className="text-[#047a4b] text-[12px] font-medium hover:text-blue-700"
                >
                  Send New
                </button>

                {/* Refresh contact list */}
                <button
                  type="button"
                  onClick={() => handleRefreshContacts(true)}
                  disabled={refreshingContacts || loading}
                  title="Refresh chats"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 7,
                    border: `1px solid ${C.border}`,
                    background: C.white,
                    color: C.greenDk,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor:
                      refreshingContacts || loading ? "not-allowed" : "pointer",
                    opacity: refreshingContacts || loading ? 0.6 : 1,
                  }}
                >
                  <RefreshCw
                    size={16}
                    className={refreshingContacts ? "animate-spin" : ""}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* ══ CHATS TAB ══ */}
          {activeTab === "chats" && (
            <>
              <div
                className="flex-1 min-h-0 overflow-y-auto hide-scrollbar"
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;

                  if (scrollHeight - scrollTop <= clientHeight + 5) {
                    fetchMoreContacts();
                  }
                }}
              >
                {chatPaged.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => handleSelectContact(c)}
                    style={rowStyle(selectedContact?.id === c.id)}
                    onMouseEnter={(e) => {
                      if (selectedContact?.id !== c.id)
                        e.currentTarget.style.background = "#f0fdf4";
                    }}
                    onMouseLeave={(e) => {
                      if (selectedContact?.id !== c.id)
                        e.currentTarget.style.background = C.white;
                    }}
                  >
                    {/* TOP ROW */}
                    <div className="flex items-center gap-2 mb-2 ">
                      {c.label?.title && (
                        <span
                          className="px-3 py-[2px] rounded-sm text-[10px] font-semibold"
                          style={{
                            background:
                              c.label.title.toLowerCase() === "lead"
                                ? "#FFF4E5"
                                : c.label.title.toLowerCase() === "promotion"
                                  ? "#E8F8EF"
                                  : c.label.title.toLowerCase() === "client" ||
                                      c.label.title.toLowerCase() === "clients"
                                    ? "#E6F4FF"
                                    : "#EEF2FF",
                            color:
                              c.label.title.toLowerCase() === "lead"
                                ? "#D97706"
                                : c.label.title.toLowerCase() === "promotion"
                                  ? "#16A34A"
                                  : c.label.title.toLowerCase() === "client" ||
                                      c.label.title.toLowerCase() === "clients"
                                    ? "#2563EB"
                                    : "#4F46E5",
                          }}
                        >
                          {c.label.title}
                        </span>
                      )}

                      {c.assigned_user && c.assigned_user !== "NA" && (
                        <div
                          className={`flex items-center ${
                            !c.label?.title ? "ml-2" : ""
                          }`}
                        >
                          <span className="text-violet-500 text-[16px]">•</span>

                          <span className="ml-1 text-[11px] text-violet-500">
                            Assign to {c.assigned_user.split("|")[0]}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* SECOND ROW */}
                    <div className="flex items-center gap-3 px-[14px]">
                      {/* Avatar */}
                      {c.contact_name &&
                      /[a-zA-Z]/.test(c.contact_name) &&
                      c.contact_name.trim().toLowerCase() !== "unknown" ? (
                        <div
                          className={`w-[38px] h-[38px] rounded-full flex items-center justify-center font-bold text-white text-[16px] shrink-0 ${getAvatarColor(
                            c.contact_name,
                          )}`}
                        >
                          {c.contact_name.trim().charAt(0).toUpperCase()}
                        </div>
                      ) : (
                        <div className="w-[38px] h-[38px] rounded-full bg-gray-400 flex items-center justify-center shrink-0">
                          <Person className="w-5 h-5 text-white" />
                        </div>
                      )}

                      {/* Name + Message */}
                      <div className="flex-1 min-w-0">
                        <div className="text-[15px] font-normal text-gray-800 truncate">
                          {c.contact_name || "Unknown"} | {c.contact_number}
                        </div>

                        <div className="text-[12px] text-gray-500 truncate mt-0.5">
                          {c.last_conversation || "No messages yet"}
                        </div>
                      </div>

                      {/* Time */}
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {Number(c.unread_count) > 0 && (
                          <span
                            className="min-w-[20px] h-[20px] rounded-full text-[11px] font-bold flex items-center justify-center px-[6px]"
                            style={{
                              background: C.green,
                              color: C.white,
                            }}
                          >
                            {c.unread_count}
                          </span>
                        )}
                        <span
                          className="text-[11px]"
                          style={{ color: C.muted }}
                        >
                          {formatTime(c.indate)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {chatFiltered.length === 0 && !loading && (
                  <div
                    className="p-8 text-center text-[13px]"
                    style={{ color: C.muted }}
                  >
                    No chats found
                  </div>
                )}
              </div>
              {loading && (
                <div className="p-3 text-center">
                  <div className="w-5 h-5 border-[3px] border-[#25d366] border-t-transparent rounded-full mx-auto animate-spin" />
                </div>
              )}
            </>
          )}

          {/* ══ CONTACTS TAB ══ */}
          {activeTab === "contacts" && (
            <>
              <div className="px-[14px] pt-[10px] pb-[6px] shrink-0">
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  All Contacts
                </span>
              </div>

              <div
                className="flex-1 min-h-0 overflow-y-auto hide-scrollbar"
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;

                  if (scrollHeight - scrollTop <= clientHeight + 5) {
                    fetchMoreContacts();
                  }
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 border-[3px] border-gray-200 border-t-[#25d366] rounded-full animate-spin" />
                  </div>
                ) : (
                  contactPaged.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => handleSelectContact(c)}
                      className="flex items-center gap-2.5 px-[14px] py-[10px] cursor-pointer transition-colors duration-150"
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        background:
                          selectedContact?.id === c.id ? "#f0fdf4" : C.white,
                        borderLeft:
                          selectedContact?.id === c.id
                            ? `3px solid ${C.green}`
                            : "3px solid transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedContact?.id !== c.id) {
                          e.currentTarget.style.background = "#f0fdf4";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedContact?.id !== c.id) {
                          e.currentTarget.style.background = C.white;
                        }
                      }}
                    >
                      <img
                        src={wpImage}
                        alt=""
                        className="w-[38px] h-[38px] rounded-full shrink-0"
                      />

                      <div className="flex-1 min-w-0">
                        <div
                          className="text-[13.5px] font-medium overflow-hidden text-ellipsis whitespace-nowrap"
                          style={{ color: C.text }}
                        >
                          {c.contact_name || "Unknown"}
                        </div>

                        <div className="text-[12px]" style={{ color: C.sub }}>
                          {c.contact_number}
                        </div>
                      </div>

                      {/* Delete btn */}
                      <button
                        onClick={() => setDeleteTarget(c)}
                        className="w-8 h-8 rounded-lg border bg-white cursor-pointer flex items-center justify-center shrink-0 transition-all duration-150"
                        style={{
                          borderColor: C.red,
                          color: C.red,
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}

                {contactFiltered.length === 0 && !loading && (
                  <div
                    className="p-8 text-center text-[13px]"
                    style={{ color: C.muted }}
                  >
                    No contacts found
                  </div>
                )}
              </div>
            </>
          )}

          {/* ADD TAB */}
          {activeTab === "add" && (
            <>
              <div className="px-[14px] pt-[10px] pb-[6px] shrink-0">
                <span
                  className="text-[13px] font-bold"
                  style={{ color: C.text }}
                >
                  {prevTab === "contacts" ? "All Contacts" : "Recent Chats"}
                </span>
              </div>

              <div
                className="flex-1 min-h-0 overflow-y-auto hide-scrollbar"
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;

                  if (scrollHeight - scrollTop <= clientHeight + 5) {
                    fetchMoreContacts();
                  }
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-6 h-6 rounded-full border-[3px] border-gray-200 border-t-[#25d366] animate-spin" />
                  </div>
                ) : (
                  addPaged.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-2.5 px-[14px] py-[10px] cursor-default"
                      style={{ borderBottom: `1px solid ${C.border}` }}
                    >
                      <img
                        src={wpImage}
                        alt=""
                        className="w-[38px] h-[38px] rounded-full shrink-0"
                      />

                      <div className="flex-1 min-w-0">
                        {/* Name + Number in single row */}
                        <div
                          className="text-[13.5px] font-semibold flex items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap"
                          style={{ color: C.text }}
                        >
                          {c.contact_name || "Unknown"}

                          <span
                            className="font-normal"
                            style={{ color: C.sub }}
                          >
                            ({c.contact_number})
                          </span>
                        </div>

                        {/* Last Message */}
                        <div
                          className="text-[12px] mt-1 overflow-hidden text-ellipsis whitespace-nowrap"
                          style={{ color: C.sub }}
                        >
                          {c.last_conversation || "No messages yet"}
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {addSourceList.length === 0 && !loading && (
                  <div
                    className="p-8 text-center text-[13px]"
                    style={{ color: C.muted }}
                  >
                    No data found
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* HEADER */}
        {showThreePanel && (
          <div className="col-[2/4] w-full h-[55px] flex items-center justify-between px-3 text-white border-b border-white/15 shrink-0 bg-[linear-gradient(135deg,_#01bd79_0%,_#018f5f_100%)]">
            {/* LEFT */}
            <div className="flex items-center gap-2.5 flex-1 overflow-hidden">
              <div className="w-[34px] h-[34px] rounded-full bg-[#dff7ea] text-[#128c7e] flex items-center justify-center font-bold text-base">
                {selectedContact?.contact_name?.charAt(0)}
              </div>

              <div className="flex flex-col min-w-0">
                {/* Name + Phone */}
                <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap">
                  <span className="text-[15px] font-medium truncate">
                    {selectedContact?.contact_name}
                  </span>

                  <span className="opacity-80">|</span>

                  <span className="text-[15px] font-medium opacity-90 truncate">
                    {selectedContact?.contact_number}
                  </span>
                </div>

                {/* Label + Assign */}
                <div className="flex items-center gap-2 mt-[2px] text-[11px]">
                  {selectedContact?.label?.title && (
                    <span className="px-2 py-[1px] rounded-sm font-semibold">
                      {selectedContact.label.title}
                    </span>
                  )}

                  {selectedContact?.assigned_user &&
                    selectedContact.assigned_user !== "NA" && (
                      <>
                        <span className="text-violet-200">•</span>

                        <span className="text-violet-100">
                          Assign to{" "}
                          {selectedContact.assigned_user.split("|")[0]}
                        </span>
                      </>
                    )}
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="w-[34px] h-[34px] rounded-md border-0 cursor-pointer flex items-center justify-center"
              >
                <Pencil size={18} color="#ffffff" />
              </button>

              <button
                onClick={() => setDeleteTarget(selectedContact)}
                className="w-[34px] h-[34px] rounded-md border-0 cursor-pointer flex items-center justify-center"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ================= CENTER CHAT PANEL ================= */}
        <div
          className={`relative overflow-hidden flex flex-col min-w-0 ${
            showThreePanel ? "col-[2] row-[2]" : "col-[2] row-[1] h-full"
          }`}
        >
          {/* Background */}
          <div
            className="absolute inset-0 bg-[#fff6e0] bg-repeat bg-center opacity-[0.7] z-0"
            style={{ backgroundImage: `url(${whatsappBg})` }}
          />

          {/* Contact Add */}
          {(!selectedContact || activeTab === "add") && (
            <div className="absolute inset-0 bg-white z-[5] overflow-auto">
              <ContactAdd userID={userID} />
            </div>
          )}

          {/* Chat */}
          {selectedContact && activeTab !== "add" && (
            <div className="absolute inset-0 z-[5] flex flex-col">
              {/* CHAT */}
              <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar">
                <ChatWindow
                  chats={chats}
                  isLoadingChats={loadingChats}
                  chatHasMore={chatHasMore}
                  fetchMoreChats={fetchMoreChats}
                />
              </div>

              {/* INPUT */}
              <div
                className="bg-white"
                style={{ borderTop: `1px solid ${C.border}` }}
              >
                <MessageInput
                  handleSendMessage={handleSendMessage}
                  handleFileSelect={handleFileSelect}
                  setMediaType={setMediaType}
                  openMediaLibrary={() => {
                    fetchUploadedMedia();
                    setShowMediaModal(true);
                  }}
                  openTemplateModal={() => {
                    const phoneId =
                      SingleWhatsApp || whatsappNumbers?.[0]?.phone_no_id || "";

                    setSingleWhatsApp(phoneId);
                    fetchVendorTemplates(phoneId);
                    setShowTemplateModal(true);
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ================= RIGHT CONTACT PANEL ================= */}

        {showThreePanel && (
          <div
            className="col-[3] row-[2] bg-white shadow-sm overflow-hidden flex flex-col"
            style={{ borderLeft: `1px solid ${C.border}` }}
          >
            {selectedContact && activeTab !== "add" && (
              <>
                {/* Contact Card */}
                <div className="p-[14px] border-b border-gray-200">
                  <div className="bg-gray-100 rounded-lg p-[14px] flex items-center gap-3">
                    <div className="w-[46px] h-[46px] rounded-full bg-[#dff7ea] text-[#128c7e] flex items-center justify-center font-bold text-[18px]">
                      {selectedContact.contact_name?.charAt(0)}
                    </div>

                    <div>
                      <div className="font-semibold text-[18px] text-gray-700">
                        {selectedContact.contact_name}
                      </div>

                      <div className="text-gray-500 text-[15px]">
                        {selectedContact.contact_number}
                      </div>
                    </div>
                  </div>
                </div>

                {/* TAGS */}
                <div className="p-4">
                  <div className="flex justify-between items-center mb-[14px]">
                    <span className="font-semibold text-[#666]">Tags</span>

                    <span className="cursor-pointer text-[18px] text-[#888]">
                      +
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {labels.map((item, index) => (
                      <div
                        key={index}
                        className="px-[14px] py-[6px] rounded-[20px] text-[13px]"
                        style={{
                          background:
                            index % 4 === 0
                              ? "#e8ecff"
                              : index % 4 === 1
                                ? "#dff7ea"
                                : index % 4 === 2
                                  ? "#fff3d8"
                                  : "#dff4ff",
                          color:
                            index % 4 === 0
                              ? "#4f46e5"
                              : index % 4 === 1
                                ? "#16a34a"
                                : index % 4 === 2
                                  ? "#ca8a04"
                                  : "#0284c7",
                        }}
                      >
                        {item.title}
                      </div>
                    ))}
                  </div>
                </div>

                {/* LABEL */}
                <div className="px-4 mt-[10px]">
                  <div className="mb-2 text-[#666] font-medium">Add label</div>

                  <select
                    value={selectedLabel}
                    onChange={(e) => {
                      setSelectedLabel(e.target.value);
                      handleAddLabel(e.target.value);
                    }}
                    className="w-full h-[42px] border border-gray-300 rounded-lg px-3 outline-none"
                  >
                    <option value="">Select Label</option>

                    {labels.map((item) => (
                      <option key={item.id} value={item.title}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ASSIGN */}
                <div className="px-4 py-[18px]">
                  <div className="mb-2 text-[#666] font-medium">
                    Assign Chat
                  </div>

                  <select
                    value={selectedPerson}
                    onChange={(e) => {
                      setSelectedPerson(e.target.value);
                      handleAssign(e.target.value);
                    }}
                    className="w-full h-[42px] border border-gray-300 rounded-lg px-3 outline-none"
                  >
                    <option value="">Chat Assign to</option>

                    {people.map((user) => (
                      <option key={user.userid} value={user.userid}>
                        {user.user_name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══ DELETE CONFIRM MODAL ══ */}
        {deleteTarget && (
          <DeleteModal
            contact={deleteTarget}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {/* Modals */}
        <MediaLibraryModal
          show={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          mediaList={mediaList}
          loadingMedia={loadingMedia}
          fetchUploadedMedia={fetchUploadedMedia}
        />

        <TemplateModal
          show={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          templates={templates}
          executeTemplate={executeTemplate}
          WhatsappNumbers={whatsappNumbers}
          SingleWhatsApp={SingleWhatsApp}
          setSingleWhatsApp={setSingleWhatsApp}
          fetchVendorTemplates={fetchVendorTemplates}
          selectedContact={selectedContact}
        />

        <EditContactModal
          show={showEditModal}
          contact={selectedContact}
          onClose={() => setShowEditModal(false)}
          onSubmit={async (data) => {
            const res = await editContact(data);

            if (res?.responseCode === "00") {
              toast.success("Contact Edited Successfully");
              setShowEditModal(false);
            }
          }}
        />

        <SendTemplateModal
          show={showSendTemplateModal}
          onClose={() => setShowSendTemplateModal(false)}
          whatsappNumbers={whatsappNumbers}
          SingleWhatsApp={SingleWhatsApp}
          setSingleWhatsApp={setSingleWhatsApp}
          templates={templates}
          fetchVendorTemplates={fetchVendorTemplates}
          executeTemplate={executeTemplate}
          userID={userID}
        />
      </div>
    </>
  );
};

export default Chats;
